import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import { createFontLink, getTranslationKey } from '../../../../modules/utils';
import { ICollectionsReducerState } from '../../../../modules/reducers';
import { getTemplatesFonts, getTemplatesList } from '../../../../modules/selectors/templates';
import {
  getAreColorPresetsFetched,
  getColorPresets
} from '../../../../modules/selectors/colorPresets';
import { fetchColorPresets, fetchTemplatesFonts } from '../../../../modules/actions';
import {
  DESIGN_COLOR_KEYS,
  DesignColorKey,
  presetsForTemplate
} from '../../../../modules/constants/colorPresets';
import { REACTION_SETS } from '../../../../modules/constants/reactionSets';
import { DEFAULT_ICON_SET, ICON_SETS } from '../../../../modules/constants/iconSets';
import { useReactionsEnabled } from '../../../../hooks/useReactionsEnabled';

type Design = ICollectionsReducerState['design'];

/**
 * Id of the photographer's edited variant of a palette. Deliberately not a hex or a palette
 * id: `applyPreset` looks options up by id, finds nothing for this one and returns without
 * writing, which is exactly right — picking the variant that is already applied is a no-op.
 */
export const CUSTOM_PRESET_VALUE = '__custom__';

interface IArgs {
  design: Design;
  templateId?: string;
  setDesign: (design: Partial<Design>) => void;
  submitColor: (...args: any[]) => void;
  availableRules: { canCustomimzeDesign?: boolean };
  setUpgradeAccountOpenState: (obj: any) => void;
}

/**
 * State, template defaults and change handlers for the gallery design form.
 *
 * Every control is gated behind the "custom design" access rule, so each handler
 * routes through `isBlocked`, which shows the upgrade modal instead of applying
 * the change.
 */
export const useDesignForm = ({
  design,
  templateId,
  setDesign,
  submitColor,
  availableRules,
  setUpgradeAccountOpenState
}: IArgs) => {
  const dispatch = useDispatch();
  const templateFonts = useSelector(getTemplatesFonts);
  const rawTemplates = useSelector(getTemplatesList);
  const allColorPresets = useSelector(getColorPresets);
  const areColorPresetsFetched = useSelector(getAreColorPresetsFetched);
  const useCustomColors = Boolean(design.newUseCustomColors);
  const showReactions = useReactionsEnabled();

  const fontOptions = useMemo(
    () =>
      orderBy(templateFonts, [({ name }) => name.toLowerCase()], ['asc']).map(({ name }) => ({
        value: name,
        label: name
      })),
    [templateFonts]
  );

  const template = useMemo(() => {
    const templates = Array.isArray(rawTemplates) ? rawTemplates : [];

    return templates.find(({ _id }) => _id === templateId);
  }, [rawTemplates, templateId]);

  const defaultValues = useMemo(
    () => ({
      font: template?.newDefaultDesign?.headingFont || template?.defaultDesign?.font || '',
      imageSpacing: template?.imageSpacing || ''
    }),
    [template]
  );

  // The title the gallery draws is set in the heading font; a gallery that never picked one
  // shows its template's.
  const titleFont = design.variables.headingFont || design.variables.font || defaultValues.font;

  // Only the palettes this template offers; none at all until both have loaded.
  const colorPresets = useMemo(
    () => (template ? presetsForTemplate(allColorPresets, template) : []),
    [allColorPresets, template]
  );

  useEffect(() => {
    if (!templateFonts.length) {
      dispatch(fetchTemplatesFonts());
    } else {
      createFontLink(templateFonts);
    }
  }, [templateFonts]); // eslint-disable-line

  // Every palette comes in one request; switching template only filters them again.
  useEffect(() => {
    if (!areColorPresetsFetched) {
      dispatch(fetchColorPresets());
    }
  }, [areColorPresetsFetched]); // eslint-disable-line

  const isBlocked = useCallback(() => {
    if (availableRules?.canCustomimzeDesign) {
      return false;
    }

    setUpgradeAccountOpenState({
      key: 'upgradeModal',
      state: true,
      desiredAction: 'customDesign'
    });

    return true;
  }, [availableRules, setUpgradeAccountOpenState]);

  /** Wraps a handler so a blocked plan gets the upgrade modal instead. */
  const guard = useCallback(
    <T>(handler: (value: T) => void) =>
      (value: T) => {
        if (!isBlocked()) {
          handler(value);
        }
      },
    [isBlocked]
  );

  // An untouched collection defaults to the first palette its template offers.
  const selectedPreset = useMemo(
    () => colorPresets.find(({ id }) => id === design.newColorPreset) || colorPresets[0],
    [colorPresets, design.newColorPreset]
  );

  /**
   * The colours actually in force: the pinned palette, with whatever the photographer has
   * changed on top. This is what the swatches have to show — a palette the picker still
   * previews unchanged after a colour was edited is simply wrong about the gallery.
   */
  const effectiveColors = useMemo(() => {
    if (!selectedPreset) {
      return undefined;
    }

    return DESIGN_COLOR_KEYS.reduce((colors, key) => {
      colors[key] = design.newVariables?.[key] || selectedPreset.colors[key];

      return colors;
    }, {} as Record<DesignColorKey, string>);
  }, [design.newVariables, selectedPreset]);

  // Case-insensitively: the picker hands back lowercase hex and the palettes are stored
  // uppercase, so re-picking a palette's own colour must not read as a change.
  const isCustomised = Boolean(
    selectedPreset &&
      effectiveColors &&
      DESIGN_COLOR_KEYS.some(
        (key) => effectiveColors[key].toLowerCase() !== selectedPreset.colors[key]?.toLowerCase()
      )
  );

  const presetOptions = useMemo(
    () => colorPresets.map((preset) => ({ value: preset.id, label: preset.name, preset })),
    [colorPresets]
  );

  /**
   * The photographer's own variant of the palette they started from, offered as an option of
   * its own so the control has something truthful to show and the change is not lost the
   * moment the menu opens. First in the list: it is the palette in force.
   */
  const customOption = useMemo(
    () =>
      isCustomised && selectedPreset && effectiveColors
        ? {
            value: CUSTOM_PRESET_VALUE,
            label: `${selectedPreset.name} - ${getTranslationKey('collectionDesign.presetCustom')}`,
            preset: { ...selectedPreset, colors: effectiveColors }
          }
        : undefined,
    [isCustomised, selectedPreset, effectiveColors]
  );

  const colorPresetOptions = useMemo(
    () => (customOption ? [customOption, ...presetOptions] : presetOptions),
    [customOption, presetOptions]
  );

  const reactionSetOptions = useMemo(
    () => REACTION_SETS.map((set) => ({ value: set.id, label: set.id, set })),
    []
  );

  const iconSetOptions = useMemo(
    () => ICON_SETS.map(({ id, labelKey }) => ({ value: id, label: getTranslationKey(labelKey) })),
    []
  );

  // Both are undefined until the palettes arrive from the backend.
  const selectedColorPreset =
    customOption ||
    colorPresetOptions.find(({ value }) => value === selectedPreset?.id) ||
    colorPresetOptions[0];
  const selectedReactionSet =
    reactionSetOptions.find(({ value }) => value === design.reactionSet) || reactionSetOptions[0];
  const selectedIconSet =
    iconSetOptions.find(
      ({ value }) => value === (design.iconSet || template?.newDefaultDesign?.iconSet)
    ) || iconSetOptions[0];

  /**
   * Falls back to the selected palette, so a colour the photographer has never touched
   * shows a real swatch instead of an empty checkerboard.
   */
  const colorValue = useCallback(
    (key: DesignColorKey) => design.newVariables?.[key] || selectedPreset?.colors[key] || '',
    [design.newVariables, selectedPreset]
  );

  // The redesign's own options — palette, custom colours, icon set, reactions — are free on
  // every plan. Only the two that predate it (font, image spacing) still route through the
  // upgrade prompt, as they do in the legacy DesignForm.
  const onColorChange = useCallback(
    (key: DesignColorKey, hex: string) => setDesign({ newVariables: { [key]: hex } }),
    [setDesign]
  );

  const onColorChangeComplete = useCallback(
    (key: DesignColorKey, hex: string) => submitColor({ [key]: hex }),
    [submitColor]
  );

  /**
   * Stores the palette id *and* its colours: the renderer then never has to resolve a
   * preset, and retuning a palette later cannot repaint galleries already published.
   *
   * Both go out in this one `setDesign` call, which the form persists as a single write.
   */
  const applyPreset = useCallback(
    (presetId?: string) => {
      const preset = colorPresets.find(({ id }) => id === presetId);

      // Nothing to apply before the palettes have loaded.
      if (!preset) {
        return;
      }

      setDesign({ newColorPreset: preset.id, newVariables: { ...preset.colors } });
    },
    [colorPresets, setDesign]
  );

  // Back to the template's default palette, dropping any colour edited on top of it.
  const resetColorPreset = useCallback(
    () => applyPreset(colorPresets[0]?.id),
    [applyPreset, colorPresets]
  );

  const selectFont = useCallback(
    guard((font: string) =>
      setDesign({ loadFonts: [font], variables: { font, headingFont: font } })
    ),
    [guard, setDesign]
  );

  const resetFont = useCallback(
    () => setDesign({ loadFonts: [], variables: { font: '', headingFont: '' } }),
    [setDesign]
  );

  const changeImageSpacing = useCallback(
    guard((imageSpacing: string) => setDesign({ imageSpacing })),
    [guard, setDesign]
  );

  const resetImageSpacing = useCallback(() => setDesign({ imageSpacing: '' }), [setDesign]);

  const selectReactionSet = useCallback(
    (reactionSet: string) => setDesign({ reactionSet }),
    [setDesign]
  );

  const selectIconSet = useCallback((iconSet: string) => setDesign({ iconSet }), [setDesign]);

  // Stored explicitly: the gallery falls back to "Minimal", not to the template, on an empty set.
  const resetIconSet = useCallback(
    () => setDesign({ iconSet: template?.newDefaultDesign?.iconSet || DEFAULT_ICON_SET }),
    [setDesign, template]
  );

  const toggleCustomColors = useCallback(
    (value: boolean) => setDesign({ newUseCustomColors: value }),
    [setDesign]
  );

  return {
    useCustomColors,
    toggleCustomColors,
    defaultValues,
    titleFont,
    fontOptions,
    colorPresetOptions,
    selectedColorPreset,
    showReactions,
    reactionSetOptions,
    selectedReactionSet,
    iconSetOptions,
    selectedIconSet,
    colorValue,
    onColorChange,
    onColorChangeComplete,
    applyPreset,
    resetColorPreset,
    selectFont,
    resetFont,
    changeImageSpacing,
    resetImageSpacing,
    selectReactionSet,
    selectIconSet,
    resetIconSet
  };
};

export default useDesignForm;
